import { addCalendarDays } from "@common/helpers/date";
import type {
    HistoryDailyCount,
    HistoryDailyCountsResponse,
} from "@common/types/auditLog";
import type { ChunkQueriesResult } from "@api/combineChunkQueries";
import type { DensityChartWindow } from "@design-system/DensityChart/DensityChart.lib";
import type { DensityChartBucketUnit } from "@design-system/DensityChart/densityBuckets.lib";
import {
    barStartDate,
    toBarIndex,
} from "@design-system/DensityChart/densityBuckets.lib";

/** Bars per counts request, inside the counts' 400-day daily and 10-year caps. */
const CHUNK_BARS: Record<DensityChartBucketUnit, number> = {
    day: 91,
    week: 52,
    month: 60,
};

/**
 * The counts requests covering `drawnDays`: whole runs of bars counted from
 * a fixed epoch, so their days, and so their query keys, stay put while the
 * chart pans. The last stops at today.
 */
export function toTimelineChunks({
    drawnDays,
    unit,
    today,
}: {
    drawnDays: DensityChartWindow;
    unit: DensityChartBucketUnit;
    today: string;
}): DensityChartWindow[] {
    const size = CHUNK_BARS[unit];
    const toChunkIndex = (date: string) =>
        Math.floor(toBarIndex({ date, unit }) / size);
    const first = toChunkIndex(drawnDays.start);
    const count = toChunkIndex(drawnDays.end) - first + 1;
    return Array.from({ length: Math.max(count, 0) }, (_, offset) => {
        const chunk = first + offset;
        const end = addCalendarDays(
            barStartDate({ index: (chunk + 1) * size, unit }),
            -1,
        );
        return {
            start: barStartDate({ index: chunk * size, unit }),
            end: end < today ? end : today,
        };
    });
}

type CountsResponse = Pick<HistoryDailyCountsResponse, "data">;

/** The chunks' rows by date, a chunk's own over another's kept from before, and whether any chunk has loaded yet. */
export function mergeChunkRows({
    responses,
    ownResponses,
}: Pick<ChunkQueriesResult<CountsResponse>, "responses" | "ownResponses">): {
    rows: HistoryDailyCount[];
    isAnyChunkLoaded: boolean;
} {
    const rowsByDate = new Map<string, HistoryDailyCount>();
    for (const response of [...ownResponses, ...responses]) {
        for (const row of response?.data ?? []) {
            if (!rowsByDate.has(row.date)) {
                rowsByDate.set(row.date, row);
            }
        }
    }
    const rows = [...rowsByDate.values()].sort((first, second) =>
        first.date.localeCompare(second.date),
    );
    return {
        rows,
        isAnyChunkLoaded: responses.some((response) => response !== undefined),
    };
}
